// import various utils
import config from 'config';
import _ from 'lodash';
import { action, autorun, computed, observable, reaction } from 'mobx';
// Import router5
import createRouter from 'router5';
import browserPlugin from 'router5/plugins/browser';
import listenersPlugin from 'router5/plugins/listeners';
import API from 'utils/api';
import Porthole from 'utils/porthole';
// Import MediahubStore
import MediahubStore from 'stores/MediahubStore';

// Verge for dimensions
import verge from 'verge';

const MOBILE_WIDTH    = config.mobileWidth;
const VALID_LANGUAGES = config.validLanguages;

// Import translations
const translations = {
  de: require('./i18n/de'),
  en: require('./i18n/en'),
  pl: require('./i18n/pl'),
  fr: require('./i18n/fr'),
};

/**
 * ViewStore Class
 *
 * It designed for handling:
 *  - routing and navigation
 *  - window and elements dimensions
 *  - various UI toggles
 *  - language and translations
 */
class ViewStore {
  router;

  // Current view with name and all route params
  @observable currentView = null;

  // Toggles
  @observable isMobileNavOpen = false;
  @observable isAdvancedOpen  = false;

  // Dimensions
  @observable containerHeight = window.innerHeight;
  @observable containerWidth  = window.innerWidth;
  @observable parentWinHeight = 0;
  @observable parentScrollTop = 0;
              updateScroll    = _.debounce(action((scroll) => {
                this.parentScrollTop = scroll;
              }), 10);
  @observable parentTopOffset = 0;
              updateTopOffset = _.debounce(action((parentTopOffset) => {
                this.parentTopOffset = parentTopOffset;
              }), 10);

  constructor() {
    // Setup Porthole
    window.windowProxy = new Porthole.WindowProxy(config.portholeProxy);
    // Add event listener
    window.windowProxy.addEventListener((msg) => this.portholeMessage(msg));

    // Initialize router
    this.router = createRouter([
      { name: 'start', path: '/' },
      { name: 'overview', path: '/:lang' },
      { name: 'channel', path: '/:lang/:channel' },
      { name: 'detail', path: '/:lang/:channel/:detail' },
    ])

    // Register plugins
      .usePlugin(browserPlugin())
      .usePlugin(listenersPlugin())

      // Register listeners
      .addListener((toState, fromState) => this.setCurrentView(toState.name, toState.params, toState.path));

    // Start the router
    this.router.start();

    // Check dimensions on each 1s interval
    window.setInterval(() => this.updateDimensions(), 1000);

    // Update parent only when containerHeight changes
    autorun(() => window.windowProxy.post({ height: this.containerHeight }));

    // Update parent with path
    reaction(
      () => this.currentView,
      () => {
        // Scroll window to top
        window.scrollTo(0, 0);
        window.windowProxy.post({ scroll: 0 });
        // Send current path to parent
        this.sendPath();
      },
    );
  }

  // For responsiveness
  @computed
  get isMobile() {
    return this.containerWidth < MOBILE_WIDTH;
  }

  // For convinience
  @computed
  get language() {
    return this.currentView ? this.currentView.lang : "";
  }

  @computed
  get currentViewName() {
    return this.currentView ? this.currentView.name : "";
  }

  @computed
  get currentPath() {
    return this.currentView ? this.currentView.path : "";
  }

  /**
   * Porthole event listener
   */
  portholeMessage(message) {
    const token        = _.result(message, 'data.tkn');
    const language     = _.result(message, 'data.language');
    const directLink   = _.result(message, 'data.directLink');
    const scroll       = _.result(message, 'data.scroll');
    const iframeOffset = _.result(message, 'data.iframeOffset');
    const winHeight    = _.result(message, 'data.winHeight');

    // Set API token
    if(token) {
      API.setToken(token);
    }

    // Set language from parent
    if(!directLink && language) {
      this.router.navigate('overview', { lang: language });
    }

    // Set directLink from parent
    if(directLink) {
      const linkParts = _.compact(_.split(directLink, '/'));
      const lang      = _.nth(linkParts, 0);
      const channel   = _.nth(linkParts, 1);
      const detail    = _.nth(linkParts, 2);
      const params    = _.omitBy({ lang, channel, detail }, _.isUndefined);

      let name;

      if(params.detail) {
        name = 'detail';
      } else {
        if(params.channel) {
          name = 'channel';
        } else {
          name = 'overview';
        }
      }

      if(!_.isEqual(directLink, window.location.pathname)) {
        this.router.navigate(name, params);
      }
    }

    // Set parent scroll position
    if(scroll) {
      this.updateScroll(scroll);
    }
    // Set iframe top offset
    if(iframeOffset) {
      this.updateTopOffset(iframeOffset);
    }
    // Set parent window height
    if(winHeight) {
      this.updateParentWinHeight(winHeight);
    }
  }

  /**
   * PORTHOLE update URL of parent with current path
   */
  sendPath() {
    let directLink = window.location.pathname;
    window.windowProxy.post({ directLink });
  }

  /**
   * ROUTING ACTIONS
   */
  @action
  setCurrentView(name, params, path) {
    const lang = this.getLanguage(_.result(params, 'lang', ''));

    if (lang === 'de') {
      if (name == 'start') {
        this.router.navigate('overview', { lang: 'de' });
        return;
      }

      const currentView = params;
      currentView.name = name;
      currentView.lang = lang;
      currentView.path = path;

      this.currentView = currentView;
      return;
    }

    const LANGUAGE_CHANNELS = {
      en: 'english',
      pl: 'polski',
      fr: 'francais',
    };

    if (name === 'channel' && params.channel !== LANGUAGE_CHANNELS.en && params.lang === 'en') {
      this.router.navigate('overview', { lang: 'en' });
      return;
    }
    if (name === 'channel' && params.channel === LANGUAGE_CHANNELS.en && params.lang !== 'en') {
      this.router.navigate('overview', { lang: params.lang });
      return;
    }

    if (name === 'channel' && params.channel !== LANGUAGE_CHANNELS.pl && params.lang === 'pl') {
      this.router.navigate('overview', { lang: 'pl' });
      return;
    }
    if (name === 'channel' && params.channel === LANGUAGE_CHANNELS.pl && params.lang !== 'pl') {
      this.router.navigate('overview', { lang: params.lang });
      return;
    }

    if (name === 'channel' && params.channel !== LANGUAGE_CHANNELS.fr && params.lang === 'fr') {
      this.router.navigate('overview', { lang: 'fr' });
      return;
    }
    if (name === 'channel' && params.channel === LANGUAGE_CHANNELS.fr && params.lang !== 'fr') {
      this.router.navigate('overview', { lang: params.lang });
      return;
    }

    // /en -> /en/english
    if (name == 'start') {
      this.router.navigate('channel', { lang, channel: LANGUAGE_CHANNELS[lang] });
      return;
    }
    if (name === 'overview' && !this.currentView) {
      this.router.navigate('channel', { lang, channel: LANGUAGE_CHANNELS[lang] });
      return;
    }

    const currentView = params;
    currentView.name = name;
    currentView.lang = lang;
    currentView.path = path;

    this.currentView = currentView;
    
  }

  /**
   * NAVIGATION HELPERS
   */
  isViewActive(to, linkChannel, linkPage) {
    if (!this.currentView) return false;
    const { name, channel, detail, page } = this.currentView;

    if(detail) {
      return _.isEqual(channel, linkChannel);
    }

    if(page) {
      return _.isEqual(page, linkPage);
    }

    return _.isEqual(name, to) && _.isEqual(channel, linkChannel);
  }

  /**
   * DIMENSIONS ACTIONS
   */
  @action
  updateDimensions() {
    const elem   = document.getElementById('root');
    const rect   = verge.rectangle(elem);
    const width  = rect.width;
    const height = rect.height;

    if(this.containerHeight !== height) {
      this.containerHeight = height;
    }

    if(this.containerWidth !== width) {
      this.containerWidth = width;
    }
  }

  @action
  updateParentWinHeight(winHeight) {
    this.parentWinHeight = winHeight;
  }


  /**
   * TOGGLES ACTIONS
   */
  @action
  toggleMobileNav(bool) {
    this.isMobileNavOpen = bool || !this.isMobileNavOpen;
  }

  @action
  toggleAdvancedSearch(bool) {
    this.isAdvancedOpen = bool || !this.isAdvancedOpen;
  }


  /**
   * LANGUAGE HELPERS
   */
  getLanguage(language) {
    language = language || this.getNavigatorLanguage();
    return _.indexOf(VALID_LANGUAGES, language) > -1
      ? language
      : _.head(VALID_LANGUAGES); // de is the default
  }

  getNavigatorLanguage() {
    const navigatorLanguage = _.head(_.compact(_.split(_.result(window, 'navigator.userLanguage'), '-', 1)))
                              || _.head(_.compact(_.split(_.result(window, 'navigator.language'), '-', 1)));

    const deReferrer = document.referrer.indexOf('/de/') > -1;

    // NOTE Just in case we need language from parent not through prothole
    // return deReferrer
    //         ? _.head(VALID_LANGUAGES) // de is the default
    //         // : navigatorLanguage;
    //         : _.last(VALID_LANGUAGES); // en as fallback

    return _.head(VALID_LANGUAGES); // de is the default
  }

  get(word) {
    return translations[this.language || 'de'][word] || `**${word}**`;
  }

  getValidLanguages() {
    return VALID_LANGUAGES;
  }
}

const viewStore = new ViewStore();
export default viewStore;



// WEBPACK FOOTER //
// ./src/stores/ViewStore.js